import { SelectChangeEvent } from '@mui/material';
import { ETranscoderSelectValueType, TranscoderAudioProfile, TranscoderProfile } from '@vxui/common';
import { EBitrateBitsMultiple, convertBitrates } from '@vxui/components';
import { TableColumn } from 'react-data-table-component';
import { audioCodecsItems, channelItems, languageItems, sampleRatesItems } from '../../../../models';
import { positiveNumber } from '../../../../utils/rules/positiveNumber';
import { AudioCodecs, Channels, SampleRates } from '../../forms/profileForm.helpers';
import {
  CellInput,
  CellLanguageInput,
  CellSelectBox,
  ColumnName,
  editRow,
  isPassthroughAudio,
} from '../profilesTable.helpers';
import { AudioTableColumnsProps } from '../profilesTable.types';

export const getColumns = ({
  t,
  actions,
  onChangeHandler,
  onSelectChangeHandler,
  editableRows,
}: AudioTableColumnsProps): TableColumn<TranscoderAudioProfile>[] => {
  let editableRow: TranscoderAudioProfile | undefined;
  return [
    {
      name: ColumnName(t, 'name'),
      selector: row => row.name,
      cell: row => {
        editableRow = editRow(row, editableRows);
        return editableRow ? (
          <CellInput
            name="name"
            defaultValue={editableRow.name ?? ''}
            error={!!editableRow.errorMsg?.name}
            placeholder={t('transcoder.forms.profile.audio.fields.name.placeholder')}
            helperText={editableRow.errorMsg?.name}
            row={editableRow}
            onChangeCellHandler={onChangeHandler}
          />
        ) : (
          row.name
        );
      },
      sortable: true,
      minWidth: '250px',
      grow: 3,
    },
    {
      name: ColumnName(t, 'codec'),
      selector: row => row.codec,
      cell: row =>
        editableRow ? (
          <CellSelectBox
            fieldName="codec"
            row={editableRow}
            value={editableRow?.codec?.toString() ?? ''}
            items={AudioCodecs}
            placeholder={t('transcoder.forms.profile.audio.fields.codec.placeholder')}
            onChangeHandler={(event: SelectChangeEvent<string>, row: TranscoderProfile) =>
              onSelectChangeHandler({ event, row, field: 'codec' })
            }
          />
        ) : (
          audioCodecsItems.find(codec => codec.value === row.codec)?.label ?? ''
        ),
      sortable: true,
      width: '100px',
    },
    {
      name: ColumnName(t, 'numberOfChannels'),
      selector: row => row.numberOfChannels || '',
      cell: row =>
        isPassthroughAudio(row) ? (
          ''
        ) : editableRow ? (
          <CellSelectBox
            fieldName="numberOfChannels"
            row={editableRow}
            value={editableRow.numberOfChannels ?? ''}
            items={Channels(t)}
            placeholder={t('transcoder.forms.profile.audio.fields.numberOfChannels.placeholder')}
            onChangeHandler={(event: SelectChangeEvent, row: TranscoderProfile) =>
              onSelectChangeHandler({ event, row, field: 'numberOfChannels', type: ETranscoderSelectValueType.number })
            }
          />
        ) : (
          channelItems(t).find(channel => channel.value === row.numberOfChannels)?.label ?? ''
        ),

      sortable: true,
      width: '150px',
    },
    {
      name: ColumnName(t, 'sampleRate', 'kHz'),
      selector: row => row.sampleRate,
      cell: row =>
        isPassthroughAudio(row) ? (
          ''
        ) : editableRow ? (
          <CellSelectBox
            fieldName="sampleRate"
            row={editableRow}
            value={editableRow.sampleRate ?? ''}
            items={SampleRates}
            placeholder={t('transcoder.forms.profile.audio.fields.sampleRate.placeholder')}
            onChangeHandler={(event: SelectChangeEvent, row: TranscoderProfile) =>
              onSelectChangeHandler({ event, row, field: 'sampleRate', type: ETranscoderSelectValueType.number })
            }
          />
        ) : (
          sampleRatesItems.find(sampleRate => sampleRate.value === row.sampleRate)?.label ?? ''
        ),
      sortable: true,
      width: '120px',
    },
    {
      name: ColumnName(t, 'bitrate', 'kbps'),
      selector: row => row.bitrate,
      cell: row =>
        isPassthroughAudio(row) ? (
          ''
        ) : editableRow ? (
          <CellInput
            name="bitrate"
            defaultValue={editableRow.bitrate ?? ''}
            error={!!editableRow.errorMsg?.bitrate}
            helperText={editableRow.errorMsg?.bitrate}
            placeholder={t('transcoder.forms.profile.audio.fields.bitrate.placeholder')}
            row={editableRow}
            onChangeCellHandler={onChangeHandler}
            {...positiveNumber()}
          />
        ) : (
          convertBitrates(row.bitrate, EBitrateBitsMultiple.kilobitPerSecond, 'divide')
        ),
      sortable: true,
      width: '120px',
    },
    {
      name: ColumnName(t, 'language'),
      selector: row => row.language || '',
      cell: row =>
        isPassthroughAudio(row) ? (
          ''
        ) : editableRow ? (
          <CellLanguageInput
            fieldName="language"
            row={editableRow}
            value={editableRow.language?.toString() ?? ''}
            placeholder={t('transcoder.forms.profile.audio.fields.language.placeholder')}
            onChangeHandler={(event: SelectChangeEvent, row: TranscoderProfile) =>
              onSelectChangeHandler({ event, row, field: 'language' })
            }
          />
        ) : (
          languageItems[row.language]?.name ?? ''
        ),
      sortable: true,
      minWidth: '150px',
      grow: 1,
    },
    {
      name: ColumnName(t, 'usage'),
      selector: row => row.templates?.length || 0,
      cell: row => row.templates?.length || 0,
      sortable: true,
      width: '70px',
    },
    ...actions,
  ];
};
